import { RouteConfig } from 'vue-router';
import { MessageBox } from '@/components';

export type Statuses = 'created' | 'expired' | 'payed' | 'rejected' | 'returned' | 'blocked';

export type PaymentsStatus = {[key in Statuses]?: string};

export type Pages = string | null;

export type Language = 'en' | 'uk' | 'ru';

export type TEffect = 'slide' | 'fade';

export type TVisible = 'visible' | 'hidden';

export type TVector = 'next' | 'prev' | null;

export type ListenerCallback = (event: Event) => void;

export type AlertType = 'error' | 'warning' | 'success' | 'info';

export interface ErrorsMessages {
  passwordNotMatch: string;
  fieldRequired: string;
  emailWrong: string;
  phoneWrong: string;
  agreementNotConfirm: string;
  innerError: string;
}

export interface PersonalDataRow {
  title: string;
  field: string;
  texts: {
    n: string;
    m: string;
    f: string;
  };
}

export interface EmailPopup {
  title: string;
  btn_text: string;
}

export interface PhonePopup {
  title: string;
  subTitle: string;
  buttonText: string;
  hint: string;
  errorEmptyCode: string;
}

export interface PersonalData {
  photo_text: string;
  rows: PersonalDataRow[];
  email_popup: EmailPopup;
  phone_popup: PhonePopup;
  edit_btn: string;
  edit_back_btn: string;
  errorsMessages: ErrorsMessages;
  resend_email: string;
  resend_successful: string;
}

export interface LoginCredentialsType {
  login: string | null;
  password: string | null;
  recaptcha: string;
  weight?: number;
}

export interface RegistrationCredentialsType {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
  password1: string;
  password2: string;
}

export enum GenderEnum {
  n,
  m,
  f,
}

// tslint:disable-next-line
export interface iUser {
  id: number;
  username: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  birthday: Date | null;
  gender: GenderEnum;
  email: string;
  phone: string;
  photo: string;
  passports: number[];
  taxpayer_id: string;
  is_email_proved: boolean;
  is_phone_proved: boolean;
  is_taxpayer_id_proved: boolean;
  is_agree_with_agreement: Date | null;
  date_joined: Date | null;
  last_login: Date | null;
  activated_at: Date | null;
  deleted_at: Date | null;
}

export type HasPermission = boolean | null | undefined;

/**
 * Representing each item for site navigation's.
 * Implement this everywhere you need a route
 *
 * @export
 * @interface Navigation
 */
export interface Navigation extends RouteConfig {
  children?: Navigation[];
  meta?: {
    link_name: string;
    meta_title: string;
    meta_desciption: string;
    permission: string;
    hasPermission: HasPermission;
  };
}

export interface Rules {
  [s: string]: (str: string | boolean, msg: string | object, min?: number, max?: number) => boolean | string;
}

export interface IPeriod {
  title: string;
  value: null | number;
}

export interface IRentPeriod {
  months: IPeriod;
  title: string;
  years: IPeriod;
}

export interface ISearchingPanel {
  headerText: {
    name: string;
    text: string;
  };
  location: {
    title: string;
    items: string[];
  };
  date: string;
  period: IRentPeriod;
  rooms: SearchingPanelRooms;
  buttonText: string;
  mobileBtnText: string;
  choosePeriod: string;
  mobPeriodTitle: string;
  minTermText: string;
  datepickerText: string;
}

export interface IActualQuery {
    start: string;
    end: string;
    rooms: number[];
    [key: string]: any;
}

export interface ILazyLoadingPhoto {
  400: string;
  800: string;
  1300: string;
  1900: string;
  3600: string;
}

export interface IAboutApartmentPhotos {
  file: ILazyLoadingPhoto;
  id: number;
  name: string;
  per_object_permissions: string[];
}

export interface IApartmentOptions {
  id: string;
  name: string;
  options: Array<Partial<IApartmentOptions>> | string[];
}

export interface IRentRuleItem {
  title: string;
  value: number;
}

export interface IRentRules {
  description: string;
  house_rules: string;
  name: string;
  rule_furniture_moving_allowed: boolean | null;
  rule_parties_allowed: boolean | null;
  rule_pets_allowed: boolean | null;
  rule_smoking_allowed: boolean | null;
  rule_subrents_allowed: boolean | null;
}

export interface IAboutApartmentContent {
  allPrice: string;
  apartment: number | object;
  apartmentView: string;
  apartment_option_categories: IApartmentOptions[];
  area: number;
  buttonPhotoName: string;
  description: string;
  descriptionTitle: string;
  design: string;
  end: string | null;
  floor: number;
  hintPromo: string;
  id: string;
  operator: number | null;
  per_object_permissions: string[];
  period?: IRentPeriod;
  photos: IAboutApartmentPhotos[];
  price: number;
  rentButtonName: string;
  rooms: number;
  rentRule: {
    firstMonth: IRentRuleItem;
    guarantee: IRentRuleItem;
  };
  rules: {
    name: string;
    options: Array<Partial<IRentRules>>;
  };
  section: string;
  similar_rent_ad_requests: Array<Partial<IAboutApartmentContent>>;
  similar_rent_ad_requests_title: string;
  slideHashName: string;
  start: string | null;
  status: string;
  title: string;
}

export interface IAboutApartmentTranslation {
  apartment_characteristic: {
    building: string;
    floor: string;
    rooms: string;
    area: string;
    apartment_street_view: string;
  };
  apartment_description: {
    title: string;
  };
  back_button_name: string;
  slider: {
    view_all_button_name: string;
  };
  rent_block: {
    start_date: string;
    period_month: string;
    period_year: string;
    rent_period: string;
    rent_per_first_month: string;
    guarantee_payment: string;
    guarantee_promo: string;
    total: string;
    button_name: string;
    hint_promo: string;
  };
  options: {
    title: string;
  };
  rules: {
    title: string;
    rules_house: string;
    rules_apartment: string;
    rule_parties_allowed: string;
    rule_pets_allowed: string;
    rule_smoking_allowed: string;
  };
  similar_rent_ad_requests: {
    title: string;
    rooms: string;
    floor: string;
    button_name: string;
  };
}

export interface IAboutApartment {
  translation: IAboutApartmentTranslation;
}

export interface IAboutApartmentData {
  price_total: number;
  currency: string;
  currency_per_month: string;
  id: string;
  price: string;
  start: string;
  end: string;
  floor: string;
  operator: number;
  photos: IAboutApartmentPhotos[];
  design: string;
  rooms: string;
  house_rules: string;
  option_categories: IApartmentOptions[];
  title: string;
  description: string;
  area: string;
  building: string;
  views: any[];
  rule_smoking_allowed: boolean | null;
  rule_pets_allowed: boolean | null;
  rule_parties_allowed: boolean | null;
  per_object_permissions: string[];
  price_guarantee_payment: number;
}

export interface IAboutApartmentPage {
  aboutApartmentData: IAboutApartmentData;
  aboutApartmentTranslation: IAboutApartmentTranslation;
  fetchAboutApartment: (id: string) => Promise<any>;
}

export interface IConfirm {
  title: string;
  subTitle: string;
  buttonText: string;
  backButtonName: string;
  hint: string;
  somethingWrong: string;
}

export interface IConfirmByPhone {
  hasErrors: boolean;
  showButton: boolean;
  throttle: {
    done: boolean;
    value: number;
    next: () => void;
  };
  resendThrottle: number[];
  userEnterPhone: string;
  t: IConfirm;
  showMessage?: boolean;
  showFormErrorMessage: (arg: any) => void;
}

export interface IConfirmEmailFormComponent {
  title: string;
  emailLabel: string;
  buttonName: string;
  errorsMessages: {
    [str: string]: string;
  };
}

export interface IErrorBucketItems {
  name: string;
  errors: string[];
}

export interface ISignUpComponent {
  t: ISignUpForm;
  formRules: Rules;
  hasErrors: boolean;
  errorBucket: Map<string, []>;
  setUserPhoneEntered: () => void;
}

export interface ISignUpForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password1: string;
  password2: string;
  isAgree: boolean;
  buttonName?: string;
  agreement?: string;
  hint?: string;
  errorsMessages: {
    passwordNotMatch: string;
    fieldRequired: string;
    emailWrong: string;
    phoneWrong: string;
    agreementNotConfirm: string;
    minLength: string;
    maxLength: string;
    innerError: string;
    passwordTooSimple: string;
    passwordContent: string;
  };
}

export interface ISignUp extends ISignUpForm {
  title: '';
}

export interface ISetPassFormComponent {
  title: string;
  password?: string;
  buttonName: string;
  passwordLabel: string;
  errorsMessages: {
    [str: string]: string;
  };
}

export interface IContainerForm {
  timeout: number;
  close: boolean;
  show: boolean;
}

export interface IQuestions {
  question: string;
  answer: string;
}

export interface IAuthorization {
  title: string;
  loginLabel: string;
  passwordLabel: string;
  forgotPassword: string;
  buttonText: string;
  hint: string;
}

/**
 * phrase used if email not passed to component
 * @example phrase: 'ваш имейл.'
 *
 * @export
 * @interface IMessageBox
 */
export interface IMessageBox {
  title: string;
  message: string;
  phrase?: string;
  errorsMessages?: {};
}

export interface IPeriodPickerPresets {
  name: string;
  value: number;
}

export interface IPeriodPicker {
  translation: {
    title: string;
    month: string;
    year: string;
    button: string;
    hint: string;
    choose: string;
    min_limit: string;
    max_limit: string;
    error_max: string;
    error_min: string;
  };
  default: number;
  value?: number;
  limits: {
    min: number;
    max: number;
  };
  presets?: IPeriodPickerPresets[];
}

export interface IPeriodPickerContainer {
  periodPickerData: IPeriodPicker;
  setPeriodPickerValue?: (value: number) => void;
}

export interface PaymentsTransaction {
  apartment: string;
  name: string;
  payment_date: string;
  total: string;
  status: string;
  action: string;
  pay: string;
  statuses: PaymentsStatus;
  details: string;
  communal_payments_include: string;
  date_until: string;
  close: string;
  back_to_payments: string;
  successful_payment: string;
  payer_title: string;
  payer: PaymentsTransactionPayer;
}

export interface PaymentsTransactionPayer {
  renter: string;
  owner: string;
  you: string;
  lodger: string;
}

export interface IPageProfileTranslation {
  contact: {};
  edit: {};
  personal_data: PersonalData;
  change_password: {};
  credit_card: {};
  my_apartments: {
    rent_ad_request: {},
    prolong_close: IPageProfileTranslationProlongClose;
    buttons: MyApartmentsButtons;
    change_terms: ChangeTerms;
  };
  notifications_input_placeholder: '';
  support: {};
  feedback: {};
  dashboard: PageProfileTranslationDashboard;
  payments: PaymentsTransaction;
}

export interface Notifications {
  count: number;
  current_page: number;
  links: object;
  next_page: number;
  previous_page: null | number;
  results: Array<{
    created_at: '',
    icon: '',
    id: '',
    is_read: false,
    per_object_permissions: string[],
    text: '',
    title: '',
  }>;
}

export interface Feedback {
  created_at: string;
  documents: [];
  id: string;
  messages: [];
  number: number;
  photos: [];
  status: string;
  status_description: string;
  text: string;
  theme_name: null | string;
  title: string;
}

export interface Payment {
  id: string;
  number: number;
  status: Statuses;
  price: number;
  type: string;
  payment_date: string;
  currency: string;
  per_object_permissions: string[];
  payer?: boolean;
}

export interface Payments {
  links: {
    next: Pages,
    previous: Pages,
  };
  count: number;
  results: Payment[];
  current_page: number;
  next_page: Pages;
  previous_page: Pages;
}

export interface QueryForTable {
  page: number;
  ordering: string;
  apartment?: string;
}
export interface  PaymentsRental {
  query: Payments;
  ordering: string;
}

export interface FilterApartmentRangeData {
  floor: [];
  price: [];
}

export interface PaymentsApartment {
  query: Payments;
  ordering: string;
}

export interface  PaymentsCommunal {
  query: Payments;
  ordering: string;
}

export interface IProfilePage {
  translation: IPageProfileTranslation;
  apartments: {};
  notifications: Notifications;
  feedbackList: {};
  feedbackItem: {};
  adCreationStepNumber: number;
  paymentsRental: PaymentsRental;
}

export interface IPage {
  titlePage: '';
  headerBgImg: {};
  aboutComplexTexts: {};
  aboutDesignTexts: {};
  aboutServicesTexts: {};
}

export interface IRecoveryPassFormComponent {
  title: string;
  email: string;
  description: string;
  agreement: string;
  buttonName: string;
  backButtonName: string;
  hint: string;
  errorsMessages: {
    [str: string]: string;
  };
}

/**
 * phrase used if email not passed to component
 * @example phrase: 'ваш имейл.'
 *
 * @export
 * @interface IRecoveryPassInfo
 */
export interface IRecoveryPassInfo {
  title: string;
  message: string;
  phrase: string;
  errorsMessages: {};
}

export interface IResetPassInfo {
  title: string;
  message: string;
  buttonName: string;
  errorsMessages: {};
}

export interface ICollectionListeners {
  type: string;
  listener: any;
}

export interface INavigation {
  class: string;
  icon: string;
  calibrateMargin: number;
  names: {
    next: string,
    prev: string,
  };
}

export interface IPagination {
  clicked: boolean;
}

export interface ISwiperOptions {
  lock: boolean;
  sensitivity: number;
  activeClass?: string;
  autoHeight: boolean;
  delay: number;
  draggable: boolean;
  effect: TEffect;
  fullHeight?: boolean;
  fullWidth: boolean;
  hashNavigation: boolean;
  height: number | string;
  linkTo: string;
  loop: boolean;
  mobile: boolean;
  navigation: INavigation;
  pagination: IPagination;
  rtl: boolean;
  slideActiveClass: string;
  slideClass: string;
  slideVisibleClass: string;
  slidesPerView: number | 'auto';
  spaceBetween: number;
  spaceBetweenSlides: number;
  speed: number;
  startIndex: number;
  width: number;
  wrapperClass: string;
  breakpoints?: {
    [key: number]: object;
  };
}

export interface ISwiperSlides {
  file: {
    '400': string;
    '800': string;
    '1300': string;
    '1900': string;
    '3600': string;
  };
  [key: string]: any;
}

export interface ISwiper {
  options: ISwiperOptions;
  slides: ISwiperSlides[];
  imageKey: string;
  readonly initOptions: ISwiperOptions;
  // previousIndex: () => number | null;
  // nextIndex: () => number | null;
  slideNext: (speed?: number, callbacks?: () => void) => Promise<boolean>;
  slidePrev: (speed?: number, callbacks?: () => void) => Promise<boolean>;
  slideTo: (index: number, speed: number, callbacks: () => void) => Promise<boolean>;
  // slideToLoop: (index: number, speed: number, callbacks: () => void) => boolean;
}

export interface Listener {
  event: string;
  callback: ListenerCallback;
}

export interface IAccount {
  translations: {
    confirm: IConfirm;
  };
}

export interface IMainPage {
  isNotEmpty: (obj: object) => boolean;
}

export interface IAboutPage {
  translation: IPageAboutTranslations;
}

export interface IPageAboutTranslations {
  headerBgImg: {};
  faq: {};
  callBack: {};
}

export interface IConfirmEmailForm {
  formRules: Rules;
  hasErrors: boolean;
  t: IConfirmEmailFormComponent;
  email: string;
  errorBucket: IErrorBucketItems[];
  sendEmail: () => Promise<void>;
}

export interface ILoginForm {
  hasErrors: boolean;
  formRules: Rules;
  errorMessageSupport: string;
  setUserPhoneEntered: (str: string) => void;
  userLogin: () => void;
  loginFomValidate: () => void;
}

export interface IComponent {
  t: IResetPassInfo;
}

export interface IRecoveryPassInfoComponent {
  t: IRecoveryPassInfo;
}

export interface IBusinessPage {
  translation: IPageBusinessTranslation;
}

export interface IPageBusinessTranslation {
  banner: {};
  form: {};
  thank_you_popup: {};
}

export interface IRecoveryPassForm {
  formRules: Rules;
  hasErrors: boolean;
  t: IRecoveryPassFormComponent;
  email: string;
  errorBucket: Map<string, string[]>;
  resetPassword: () => Promise<void>;
}

export interface ISetPassForm {
  formRules: Rules;
  t: ISetPassFormComponent;
  email: string;
  errorBucket: Map<string, string[]>;
  sendPassword: () => Promise<void>;
}

export interface IRegistrationTranslation {
  login: IAuthorization;
  confirm: IConfirm;
  confirm2fa: IConfirm;
  signUp: ISignUp;
  passRecover: IRecoveryPassFormComponent;
  setPassword: ISetPassFormComponent;
  messageSendMail: IRecoveryPassInfo;
  messageSuccess: IResetPassInfo;
  confirmEmail: IConfirmEmailFormComponent;
  messageSuccessEmailConfirm: IResetPassInfo;
  messageConfirmPhone: IMessageBox;
  messageEmailSuccess: IMessageBox;
  messageEmailFailed: IMessageBox;
  passwordResetSuccess: IMessageBox;
}

export interface IRegistration {
  translation: IRegistrationTranslation;
  userPhone: string;
  formErrorMessage: string;
  permissions: string[];
}

export interface IPageNewsTranslation {
  titlePage: '';
  headerBgImg: {};
  filers: {};
  promo_text: '';
  pagination_btn_text: '';
  similar_title: '';
  similar_link_text: '';
}

export interface IAction {
  type: string;
  payload?: any;
}

export interface ISortItem {
  name: string;
  value: string;
}

export interface ICatalogListTexts {
  countText: string;
  clearFilters: string;
  sortCount: {
    title: string;
    items: number[];
  };
  sortTypes: {
    title: string;
    items: ISortItem[];
  };
  emptyCatalog: {
    firstLineText: string;
    secondLineText: string;
  };
  mobTitleText: string;
}

export interface ITranslation {
  aboutApartment: IAboutApartmentTranslation;
  additionalFilters: {};
  catalogItem: {};
  catalogListTexts: ICatalogListTexts;
  filtersApartment: {};
  pagination: {};
  title: string;
  checkout: {};
}

export interface ICatalogList {
  count: number;
  next: string;
  previous: string;
  results: Array<{}>;
}

export interface ICatalogPage {
  catalogListData: ICatalogList;
  translation: ITranslation;
  aboutApartment: IAboutApartmentData; // FIXME: create types
}

export interface Errors {
  field: string;
  fieldTranslation?: string;
  messages: string[];
}

export interface Alert {
  type: AlertType;
  message: string;
}

export interface Cell {
  name: string;
  type: string;
  orderBy: string;
  slot?: any;
}

export enum Profile {
  SET_COMMUNAL_ORDERING = 'SET_COMMUNAL_ORDERING',
  ADD_STEP_NUMBER = 'ADD_STEP_NUMBER',
  CLEAR_COMUNAL_PAYMENTS = 'CLEAR_COMUNAL_PAYMENTS',
  CLEAR_FEEDBACK_ITEM = 'CLEAR_FEEDBACK_ITEM',
  CLEAR_NOTIFICATIONS = 'CLEAR_NOTIFICATIONS',
  CLEAR_RENTAL_PAYMENTS = 'CLEAR_RENTAL_PAYMENTS',
  CLEAR_STEP_NUMBER = 'CLEAR_STEP_NUMBER',
  DEC_STEP_NUMBER = 'DEC_STEP_NUMBER',
  READ_NOTIFICATIONS = 'READ_NOTIFICATIONS',
  CHECK_NOTIFICATIONS = 'CHECK_NOTIFICATIONS',
  SET_COMMUNAL_PAYMENTS = 'SET_COMMUNAL_PAYMENTS',
  SET_FEEDBACK_ITEM = 'SET_FEEDBACK_ITEM',
  SET_FEEDBACK_LIST = 'SET_FEEDBACK_LIST',
  SET_NOTIFICATIONS = 'SET_NOTIFICATIONS',
  SET_PROFILE_APARTMENTS = 'SET_PROFILE_APARTMENTS',
  SET_PROFILE_TRANSLATION = 'SET_PROFILE_TRANSLATION',
  SET_RENTAL_PAYMENTS = 'SET_RENTAL_PAYMENTS',
  TOGGLE_FAVORITE_APARTMENTS = 'TOGGLE_FAVORITE_APARTMENTS',
  UPDATE_APARTMENTS = 'UPDATE_APARTMENTS',
  UPDATE_MESSAGES = 'UPDATE_MESSAGES',
  SET_RENTAL_ORDERING = 'SET_RENTAL_ORDERING',
  SET_APARTMENT_ORDERING = 'SET_APARTMENT_ORDERING',
  CLEAR_APARTMENT_PAYMENTS = 'CLEAR_APARTMENT_PAYMENTS',
  SET_APARTMENT_PAYMENTS = 'SET_APARTMENT_PAYMENTS',
  UPDATE_COMMUNAL_PAYMENTS = 'UPDATE_COMMUNAL_PAYMENTS',
  UPDATE_RENTAL_PAYMENTS = 'UPDATE_RENTAL_PAYMENTS',
  UPDATE_RENT_AD_REQUEST = 'UPDATE_RENT_AD_REQUEST',
  UPDATE_APARTMENT_PAYMENTS = 'UPDATE_APARTMENT_PAYMENTS',
}

export type ApartmentRule = 'rule_pets_allowed' | 'rule_smoking_allowed' | 'rule_parties_allowed';

export interface MappedApartmentRule {
  name: string;
  value: boolean;
}

export type MappedApartmentRules = MappedApartmentRule[];

export interface IFiltersItems {
  id: number;
  name: string;
}

export type Rule = 'rule_pets_allowed' | 'rule_smoking_allowed' | 'rule_parties_allowed';
export interface IRules {
  name: Rule;
  label: string;
}
export interface IAdditionalFilters {
  type: 'boolean' | 'array';
  title: string;
  name?: string;
  items: IFiltersItems[] | IRules[];
}
export interface IFilters {
  price: {
    label: string;
    label_mobile: string;
    label_mini: string;
    min: number;
    max: number;
  };
  design: {
    label: string;
    items: IFiltersItems[];
  };
  floor: {
    label: string;
    min: number;
    max: number;
  };
  additional_filters: IAdditionalFilters[];
}

export interface VerifyLodger {
  text: string;
  errorText: string;
  btnText: string;
}

export interface MessageBoxMessage {
  title: string;
  text: string;
  link: string;
  buttonText: string;
}

export interface MessageBoxVerify {
  success: MessageBoxMessage;
  error: MessageBoxMessage;
}

export interface TranslationSettingsHeader {
  logo_title: string;
  mobMenuLangText: string;
  logout: string;
}

export interface TranslationServerError {
  retry: string;
  text: string;
  btn: string;
}

export interface TranslationFieldError {
  required: string;
  conditions: string;
  email: string;
  phone: string;
}

export interface PageNotFound {
  btn: string;
  text: string;
}

export interface ITranslationSettings {
  basic_error_title: string;
  period_picker: any;
  searchingPanel: ISearchingPanel;
  payment_screen: PaymentsScreenTexts;
  verify_lodger: VerifyLodger;
  verify_email: MessageBoxVerify;
  header: TranslationSettingsHeader;
  server_error: TranslationServerError;
  field_errors: TranslationFieldError;
  page_not_found: PageNotFound;
  location: string;
}

export interface Settings {
  chooseButtonLinkQueryString: string;
  filters: IFilters;
  errorMessage: string;
  contact_us: {
    PHONE_NUMBER?: string,
    EMAIL?: string,
  };
  follow_us: {};
  navigation: any;
  language: Language;
  showFooter: boolean;
  searchingPanelState: {
    startDate: string,
    actualNumbersRooms: string[],
  };
  sms_resend_throttle: number[];
  actualSearchData: {};
  mobMenuLangText: '';
  promo: {};
  promoTitle: '';
  rfp: number;
  mobile_app_links: object;
  apartmentRules: Rule[];
}

export interface ErrorMessage {
  name: string;
  error: string[];
}

export interface Messages {
  centralAlert: boolean;
}

export interface IState {
  periodPicker: IPeriodPicker;
  settings: Settings;
  userErrorMessage: string;
}

export interface IActionCardLink {
  text: string;
  url: string;
  type: string;
}

export interface IActionCard {
  links: IActionCardLink[];
  image: ILazyLoadingPhoto;
  title: string;
  description: string;
}

export interface ToBusinessData {
  name: string;
  email: string;
  phone: string;
}

export interface FiltersDesign {
  label: string;
  items: FiltersDesignItem[];
}

export interface FiltersDesignItem {
  id: number;
  name: string;
}

export interface SettingsTranslations {
  header: {
    logo_title: string;
  };
  footer: {
    logo_title: string;
  };
  index: {
    hello: string;
  };
  about_project: {
    thank_you: string;
  };
  main_page: {
    thank_you: string;
  };
  rent_ad_page: {
    conveniences_title: string;
    rules_of_living_title: string;
    rules_of_living_house: string;
    rules_of_living_apartment: string;
  };
  period_picker: {
    title: string;
    month: string;
    year: string;
    hint: string;
    choose: string;
    button: string;
    presets: PeriodPickerPresets[];
    limits: {
      min: number;
    }
  };
  searchingPanel: ISearchingPanel;
  page_not_found: {
    text: string;
    btn: string;
  };
  server_error: TranslationServerError;
  field_errors: {
    min_length: string;
    max_length: string;
    conditions: string;
    email: string;
    required: string;
    phone: string;
  };
  verify: {
    text: string;
    errorText: string;
    btnText: string;
  };
  payment_screen: PaymentsScreenTexts;
}

interface PeriodPickerPresets {
  value: number;
  name: string;
}

interface SearchingPanelRooms {
  title: string;
  value: string[];
}

interface ModalDelete {
  text: string;
  btnText: string;
  btnCancel: string;
}

interface ModalAddThankYou {
  text: string;
  btnText: string;
}

interface ModalAdd {
  text: string;
  error: string;
  placeholder: string;
  btnText: string;
}

export interface LodgersBlock {
  title: string;
  owner: string;
  lodger: string;
  renter: string;
  add: string;
  back: string;
  showAll: string;
  modalAdd: ModalAdd;
  modalAddThankYou: ModalAddThankYou;
  modalDelete: ModalDelete;
  modalDeleteThankYou: ModalAddThankYou;
}

export interface PageProfileTranslationDashboard {
  title: string;
  apartmentsBlock: {};
  notificationsBlock: {};
  paymentsBlock: {};
  rulesBlock: {};
  servicesBlock: {};
  lodgersBlock: LodgersBlock;
  statsBlock: {};
}

export interface AdditionalFiltersItem {
  type: string;
  title: string;
  name: string;
  items: AdditionalFiltersItemContent[];
}

export interface AdditionalFiltersItemContent {
  id: number;
  default: boolean;
  name: string;
}

export interface AdditionalFiltersData {
  options?: string[];
  apartment_views?: string[];
}

export interface CatalogQuery {
  start?: string;
  end?: string;
  rooms?: string;
  design?: string;
  max_price?: string;
  min_price?: string;
  max_floor?: string;
  min_floor?: string;
  options?: string;
  apartment_views?: string;
  page_size?: string;
  ordering?: string;
  page?: string;
}

export interface ApartmentData {
  apartment_area: string;
  contract_number: string;
  cover: object;
  design_name: string;
  design_photos: object[];
  favourite: boolean;
  floor_number: number;
  id: number;
  in_rent: boolean;
  in_rent_contract: string;
  in_rent_end_date: string | null;
  in_rent_income: string;
  in_rent_start_date: string | null;
  lodgers: [];
  number: number;
  options: [];
  rooms: number;
  section: string;
  user_role: string;
  lease_contract: {
    per_object_permissions: string[];
  } | null;
  rent_ad_request: {
    id: string;
    status: string;
    per_object_permissions: string[];
  } | null;
  per_object_permissions: string[];
}

export interface CheckoutTranslations {
  TopImageBlockCatalog: {
    image: null | string;
    title: string;
    description: string;
    links: [];
  };
  apartment_block: {
    title: string;
    section: string;
    floor: string;
    rooms: string;
    area: string;
  };
  rules_block: {
    title: string;
    rule_parties_allowed: string;
    rule_pets_allowed: string;
    rule_smoking_allowed: string;
  };
  user_data_block: LodgerData;
  documents_block: {
    title: string;
    text: string;
    documents: {}
    agree: string;
    agreeRules: string;
  };
  price_block: {
    start_date: string;
    rent_period: string;
    period_month: string;
    period_year: string;
    rent_per_first_month: string;
    guarantee_payment: string;
    guarantee_promo: string;
    total: string;
    currency_month: string;
    currency: string;
    title: string;
  };
  foreign_block: {
    text_one: string;
    text_two: string;
  };
  buttons: {
    back: string;
    next: string;
  };
}

export interface RentersTranslations {
  headerBgImage: {};
  faq: {};
  howItWorks: HowItWorksTexts;
  callback: {};
}

export interface HowItWorksTexts {
  whyUsBlock: IActionCard;
  whatYouGet: {
    title: string;
    items: {
      first: WhatYouGetItem;
      second: WhatYouGetItem;
      third: WhatYouGetItem;
    };
  };
  slides: {
    key: string;
    title: string;
    slides: SlidesItem[];
  };
  list2x2: {
    title: string;
    subTitle: {
      first: string;
      second: string;
    };
    items: List2x2Item[];
  };
}

export interface WhatYouGetItem {
  title: string;
  text: string;
}

export interface List2x2Item {
  title: string;
  description: string;
}

export interface SlidesItem {
  id: number;
  image: ILazyLoadingPhoto;
  title: string;
  description: string;
}

export interface VuetifyInput extends Element {
  errorBucket: string[];
  shouldValidate: boolean;
  valid: boolean;
  error: boolean;
  validate: (force: boolean, value?: any) => boolean;
  reset: () => void;
  lazyValue: string;
  label: string;
  hasError: boolean;
}

export interface FormValidate {
  hasErrors: boolean;
  bucket: Errors[];
}

export interface LodgerData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  description: string;
  per_object_permissions: string[];
}

export interface PaginationProps {
  pages: number;
  active: number;
  clicked: undefined | boolean;
}

export interface PaginationListeners {
  [p: string]: (arg: number) => void;
}


export interface IPageProfileTranslationProlongClose {
  prolong: {
    title: string;
    subtitle: string;
    new_start_label: string;
    period_label: string;
    check_data_subtitle: string;
    apartment_label: string;
    new_lease_period_label: string;
    rent_price: string;
    buttons: {
      cancel: string;
      send_request: string;
    };
    info_block: {
      title: string;
      text: string;
      phone: string;
      email: string;
    };
    thank_you: {
      text_first: string;
      text_second: string;
      button: string;
    };
  };
  close: {
    title: string;
    subtitle: string;
    old_end_label: string;
    new_end_label: string;
    reason_subtitle: string;
    reason_placeholder: string;
    new_lease_period_label: string;
    buttons: {
      cancel: string;
      send_request: string;
    };
    info_block: {
      title: string;
      text: string;
      text_second: string;
      subtitle: string;
      phone: string;
      email: string;
    };
    thank_you: {
      text_first: string;
      text_second: string;
      button: string;
    };
  };
}

export interface PaymentsScreenTexts {
  total_text: string;
  card_text: string;
  ukrainian_card_text: string;
  buttons: {
    back: string;
    pay: string;
    change_card: string;
  };
  foreign_card_text: string;
  payment_info: {
    title: string;
    text_one: string;
    text_two: string;
  };
  currency: string;
  TopImageBlockPayment: {
    image: null | string;
    title: string;
    description: string;
    links: string[];
  };
}

export interface PaymentsInfoTexts {
  info: {
    first: string;
    second: string;
  } | null;
  warning: {
    first: string;
    second: string;
  } | null;
}

export interface MyApartmentsButtons {
  back_btn: string;
  back_btn_text: string;
  create_ad_btn: string;
  edit_ad_btn: string;
  prolong_btn: string;
  close_btn: string;
  change_terms_btn: string;
  detail_btn: string;
  deactivate_btn: string;
  payments_btn: string;
}

export interface ChangeTerms {
  title: string;
  subtitle: string;
  info: string;
  info_block: {
    title: string;
    text: string;
    phone: string;
    email: string;
  };
  price_block: {
    title: string;
    description: string;
    recommended_price: string;
    price_hint: string;
    rent_price: string;
    currency: string;
    standard_one_service: string;
    month_income: string;
    tax_hint: string;
  };
  date_block: {
    start_date: string;
    end_date: string;
  };
  check_data_subtitle: string;
  apartment_label: string;
  new_lease_period_label: string;
  rent_price: string;
  buttons: {
    cancel: string;
    send_request: string;
  };
}

export type OnModerationStatuses = 'processing' | 'new' | 'reopened';
export type OutOfPoolStatuses = 'cancelled' | 'declined';

export type ApartmentUserRole = 'future_renter' | 'lodger' | 'renter' | 'owner' | 'unknown';
export type ProfileMemoType = 'FULL' | 'SHORT';

export interface NewRenterBlock {
  title: string;
  steps: NewRenterBlockStep[];
  questions: Questions;
}

export interface Questions {
  title: string;
  phone: string;
  email: string;
}

export interface NewRenterBlockStep {
  number: string;
  content: string;
}

export interface TipsBlock {
  title: string;
  specialists: string;
  favours: string;
  storage: string;
  news: string;
}

export interface ProfileRulesBlock {
  title: string;
  items: ProfileRulesBlockItem[];
}

export interface ProfileRulesBlockItem {
  text: string;
  bool: boolean;
}
export type UserDescription = 'lodger' | 'renter' | 'owner';

export interface InfinityQuery {
  search?: string;
  ordering?: string;
  page?: number | null;
}

export interface FeedbackList {
  search?: string;
  ordering?: string;
  count: number;
  current_page: number;
  links: object;
  next_page: null | number;
  previous_page: null | number;
  results: Feedback[];
}

export interface GeTokenData {
  paymentTypes: PaymentTypes;
  priorityPaymentTypes: PriorityPaymentTypes;
  payee: Payee;
  order: Order;
  payer: Payer;
  token: Token;
}

export interface Token {
  tokenFlag: string;
  returnToken: string;
  token: string;
  cardMask: string;
  otherPaymentMethods: string;
}

export interface Payer {
  lang: string;
}

export interface Order {
  shopOrderNumber: string;
  billAmount: string;
  successUrl: string;
  failureUrl: string;
  preauthFlag: string;
  billCurrency: string;
  attribute1: string;
  description: string;
}

export interface Payee {
  payeeId: string;
}

export interface PriorityPaymentTypes {
  card: string;
  portmone: string;
  token: string;
  masterpass: string;
  visacheckout: string;
  createtokenonly: string;
  qr: string;
}

export interface PaymentTypes {
  card: string;
  portmone: string;
  token: string;
  masterpass: string;
  visacheckout: string;
  createtokenonly: string;
}

export interface BowserPrser {
  browser: {
    name: string;
    version: string;
  };
  os: {
    name: string;
    version: string;
    versionName: string;
  };
  platform: {
    type: string;
  };
  engine: {
    name: string;
    version: string;
  };
}
